import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const SeniorityDistributionChart = ({ data, description }) => {
  const seniorityData = () => {
    const levels = {};
    
    // Only include hired candidates with role data
    data.filter(candidate => 
      candidate.stage === 'Hired' && 
      candidate.role && 
      candidate.role.trim() !== ''
    ).forEach(candidate => {
      const level = candidate.seniority || 'Not Specified';
      levels[level] = (levels[level] || 0) + 1;
    });
    
    return Object.entries(levels)
      .map(([level, count]) => ({
        name: level,
        value: count,
        percentage: ((count / data.filter(c => c.stage === 'Hired').length) * 100).toFixed(1)
      }))
      .sort((a, b) => b.value - a.value);
  };

  // Color palette for different seniority levels
  const COLORS = {
    'Junior': '#3b82f6',
    'Mid': '#22c55e', 
    'Senior': '#f59e0b',
    'Lead': '#ef4444',
    'Not Specified': '#6b7280'
  };

  const getColor = (name) => COLORS[name] || '#8b5cf6';

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="font-semibold">{data.name}</p>
          <p className="text-sm text-gray-600">
            Count: {data.value} ({data.percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const chartData = seniorityData();
  const totalHired = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="dashboard-card p-6 slide-in-up" style={{animationDelay: '1100ms'}}>
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">Seniority Level Distribution</h3>
          <p className="text-sm text-gray-500">Experience levels of hired candidates</p>
        </div>
        <div className="text-right">
          <div className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-1 rounded-full mb-1">
            Experience
          </div>
          <p className="text-xs text-gray-500">{totalHired} hired candidates</p>
        </div>
      </div>
      {description && (
        <p className="text-sm text-gray-600 mb-4">{description}</p>
      )}
      
      <div className="flex flex-col lg:flex-row items-center gap-6">
        {/* Pie Chart */}
        <div className="flex-1 min-w-0">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={100}
                paddingAngle={3}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getColor(entry.name)} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        
        {/* Custom Legend */}
        <div className="flex-shrink-0 w-full lg:w-auto">
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Experience Levels</h4>
            {chartData.map((entry, index) => (
              <div key={entry.name} className="flex items-center justify-between gap-4 min-w-[200px]">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full flex-shrink-0" 
                    style={{ backgroundColor: getColor(entry.name) }}
                  />
                  <span className="text-sm text-gray-700 font-medium">
                    {entry.name}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-gray-800">
                    {entry.value}
                  </div>
                  <div className="text-xs text-gray-500">
                    {entry.percentage}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeniorityDistributionChart;